import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, XCircle, Play, Square, Loader2, FileText, Save } from 'lucide-react';
import { toast } from "sonner";
import axios from "axios";
import { API } from '../utils';

export const TechnicianNotesEditor = ({ appointmentId, initialValue, onSaved }) => {
  const [value, setValue] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  const dirty = value !== (initialValue || "");

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/appointments/${appointmentId}/technician-notes`, {
        technician_notes: value,
      });
      toast.success("Note salvate");
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="technician-notes-editor">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={16} className="text-blue-700" />
        <label className="text-sm font-semibold text-blue-900">Note tecnico</label>
        <span className="text-xs text-blue-600">(visibili a operatori e admin)</span>
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Aggiungi note relative all'intervento..."
        className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white"
        rows={3}
        data-testid="technician-notes-input"
      />
      <div className="flex justify-end mt-2">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
          data-testid="save-technician-notes-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {dirty ? "Salva" : "Salvato"}
        </button>
      </div>
    </div>
  );
};

const WorkTrackingPanel = ({ appointment, workTracking, canStartStopWork, currentUser, onUpdate }) => {
  const { t } = useTranslation();
  const {
    workInProgress, elapsedTime, formatElapsedTime,
    startingWork, endingWork, gettingLocation,
    showEndWorkForm, setShowEndWorkForm,
    endWorkStatus, setEndWorkStatus,
    endWorkReason, setEndWorkReason,
    endWorkReasonCategory, setEndWorkReasonCategory,
    handleStartWork, handleEndWork,
  } = workTracking;

  if (!canStartStopWork) return null;

  return (
    <div className="border-t pt-4 mt-4">
      {/* Work In Progress - Show Timer */}
      {workInProgress && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-semibold text-green-800">
                {t('appointment.workInProgress') || 'Lavoro in corso'}
              </span>
            </div>
            <span className="text-2xl font-mono font-bold text-green-700" data-testid="work-timer">
              {formatElapsedTime(elapsedTime)}
            </span>
          </div>

          {appointment.work_start_location && (
            <p className="text-xs text-green-600 mb-3">
              GPS registrato all'avvio: {appointment.work_start_location.lat?.toFixed(5)}, {appointment.work_start_location.lon?.toFixed(5)}
            </p>
          )}

          {!showEndWorkForm ? (
            <button onClick={() => setShowEndWorkForm(true)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              data-testid="stop-work-btn">
              <Square size={18} />
              {t('appointment.stopWork') || 'Termina Lavoro'}
            </button>
          ) : (
            <div className="space-y-3 bg-white p-3 rounded-lg border border-green-200">
              <p className="font-medium text-gray-800">
                {t('appointment.endWorkTitle') || 'Come si e concluso il lavoro?'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setEndWorkStatus("completed")}
                  className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                    endWorkStatus === "completed" ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-green-300"
                  }`}
                  data-testid="end-work-completed">
                  <Check className="text-green-500" size={24} />
                  <span className="text-xs font-medium">Completato</span>
                </button>
                <button onClick={() => setEndWorkStatus("not_completed")}
                  className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                    endWorkStatus === "not_completed" ? "border-red-500 bg-red-50" : "border-gray-200 hover:border-red-300"
                  }`}
                  data-testid="end-work-not-completed">
                  <XCircle className="text-red-500" size={24} />
                  <span className="text-xs font-medium">Non completato</span>
                </button>
              </div>
              {endWorkStatus === "not_completed" && (
                <div className="space-y-2" data-testid="not-completed-section">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Causale mancato completamento <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={endWorkReasonCategory || ""}
                      onChange={(e) => setEndWorkReasonCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      data-testid="end-work-reason-category"
                    >
                      <option value="">Seleziona causale...</option>
                      <option value="cliente_assente">Cliente assente</option>
                      <option value="veicolo_non_disponibile">Veicolo non disponibile</option>
                      <option value="materiale_mancante">Materiale/Dispositivo mancante</option>
                      <option value="dato_errato">Dati errati o mancanti</option>
                      <option value="impossibilita_tecnica">Impossibilita tecnica</option>
                      <option value="annullato_cliente">Annullato dal cliente</option>
                      <option value="condizioni_meteo">Condizioni meteo</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                  <textarea
                    value={endWorkReason}
                    onChange={(e) => setEndWorkReason(e.target.value)}
                    placeholder="Dettagli aggiuntivi (obbligatorio)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    rows={3}
                    data-testid="end-work-reason"
                  />
                  {(!endWorkReasonCategory || !endWorkReason.trim()) && (
                    <p className="text-xs text-red-600">Sia la causale che il dettaglio sono obbligatori per confermare &quot;Non completato&quot;</p>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => { setShowEndWorkForm(false); setEndWorkStatus("completed"); setEndWorkReason(""); }}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                  {t('common.cancel')}
                </button>
                <button onClick={handleEndWork} disabled={endingWork || gettingLocation}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  data-testid="confirm-end-work-btn">
                  {(endingWork || gettingLocation) ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <><Square size={16} />{t('appointment.confirmEndWork') || 'Conferma'}</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Technician Notes - editable by assigned installer at any phase */}
      {currentUser?.role === "installer" && appointment.assigned_installer_id === currentUser?.id && (
        <TechnicianNotesEditor
          appointmentId={appointment.id}
          initialValue={appointment.technician_notes || ""}
          onSaved={onUpdate}
        />
      )}

      {/* Start Work Button */}
      {!workInProgress && ["scheduled", "confirmed", "provisional"].includes(appointment.status) && (
        <button onClick={handleStartWork} disabled={startingWork || gettingLocation}
          className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50"
          data-testid="start-work-btn">
          {(startingWork || gettingLocation) ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              {gettingLocation ? (t('appointment.gettingLocation') || 'Rilevamento GPS...') : (t('common.loading') || 'Caricamento...')}
            </>
          ) : (
            <><Play size={18} />{t('appointment.startWork') || 'Inizia Lavoro'}</>
          )}
        </button>
      )}

      {/* Work Completed Summary */}
      {(appointment.status === "completed" || appointment.status === "pending_validation" || appointment.status === "not_completed") && appointment.work_started_at && (
        <div className={`p-4 rounded-lg ${
          appointment.status === "completed" ? "bg-green-50 border border-green-200"
          : appointment.status === "pending_validation" ? "bg-amber-50 border border-amber-200"
          : "bg-red-50 border border-red-200"
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {appointment.status === "completed" ? <Check className="text-green-600" size={20} />
             : appointment.status === "pending_validation" ? <Loader2 className="text-amber-600 animate-spin" size={20} />
             : <XCircle className="text-red-600" size={20} />}
            <span className={`font-semibold ${
              appointment.status === "completed" ? "text-green-800"
              : appointment.status === "pending_validation" ? "text-amber-800"
              : "text-red-800"
            }`}>
              {appointment.status === "completed" ? (t('appointment.workCompleted') || 'Lavoro completato e validato')
               : appointment.status === "pending_validation" ? 'Lavoro terminato - In attesa validazione Salesforce'
               : (t('appointment.workNotCompleted') || 'Lavoro non completato')}
            </span>
          </div>
          {appointment.status === "pending_validation" && (
            <p className="text-xs text-amber-700 mb-2">
              L&apos;appuntamento diventera &quot;Eseguito&quot; (verde) automaticamente quando il job sara marcato come Complete su Salesforce.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">{t('appointment.startTime') || 'Inizio'}</p>
              <p className="font-medium">{new Date(appointment.work_started_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</p>
            </div>
            {appointment.work_ended_at && (
              <div>
                <p className="text-gray-500">{t('appointment.endTime') || 'Fine'}</p>
                <p className="font-medium">{new Date(appointment.work_ended_at).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}</p>
              </div>
            )}
            {appointment.actual_duration && (
              <div className="col-span-2">
                <p className="text-gray-500">{t('appointment.actualDuration') || 'Durata effettiva'}</p>
                <p className="font-bold text-lg">
                  {Math.floor(appointment.actual_duration / 60) > 0 && `${Math.floor(appointment.actual_duration / 60)}h `}
                  {appointment.actual_duration % 60}min
                </p>
              </div>
            )}
          </div>
          {(appointment.work_start_location || appointment.work_end_location) && (
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
              {appointment.work_start_location && <p>Inizio: {appointment.work_start_location.lat?.toFixed(5)}, {appointment.work_start_location.lon?.toFixed(5)}</p>}
              {appointment.work_end_location && <p>Fine: {appointment.work_end_location.lat?.toFixed(5)}, {appointment.work_end_location.lon?.toFixed(5)}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkTrackingPanel;
